import React from 'react';
import { Box, Typography, Stack, Divider, Paper, Alert, IconButton, Tooltip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import { RootState } from '../../../../core/store';
import { useSelector } from 'react-redux';
import { usePartners } from '../../../../core/hooks/usePartners';
import { Partner } from '../../../../core/types/partner';
import { UI_DAYS_REVERSE_MAP, TIME_SLOTS_REVERSE_MAP } from '../../../../core/constant/constant';
import moment from 'moment';
import { useProfileQuery } from '../../../auth/hooks/useProfileQuery';

interface ReviewStepProps {
  isSaving: boolean;
  isMobile?: boolean;
  onEditSection?: (stepIndex: number) => void;
}

const STEP_INDICES = {
  BASIC_CONTACT: 0,
  PERSONAL_DETAILS: 1,
  PREFERENCE: 2,
  SCHEDULE: 3,
  EMERGENCY_CONTACT: 4,
};

const ReviewStep: React.FC<ReviewStepProps> = ({
  isSaving = false,
  isMobile = false,
  onEditSection,
}) => {
  const { partners } = usePartners();
  const basicInfo = useSelector((state: RootState) => state.basicInfo);
  const personalDetails = useSelector((state: RootState) => state.personalDetails);
  const availability = useSelector((state: RootState) => state.availability);
  const preferenceInformation = useSelector((state: RootState) => state.preferenceInformation);
  const emergencyContact = useSelector((state: RootState) => state.emergencyContact);

  const memberPartner = partners.find((partner: Partner) => partner._id === basicInfo.partnerId);

  const SectionHeader = ({ title, stepIndex }: { title: string; stepIndex: number }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
      <Typography variant='subtitle1' fontWeight='bold' color='primary.main'>
        {title}
      </Typography>
      {onEditSection && (
        <Tooltip title={`Edit ${title}`}>
          <IconButton
            size='small'
            onClick={() => onEditSection(stepIndex)}
            aria-label={`Edit ${title}`}
            disabled={isSaving}
            sx={{
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'rgba(92, 90, 240, 0.08)',
              },
            }}
            data-testid={`edit-section-${stepIndex}`}
          >
            <EditIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <Box>
      {!isMobile && (
        <Box sx={{ mb: 3 }}>
          <Typography variant='h5' color='text.primary' sx={{ mb: 1 }}>
            Complete Your Enrollment
          </Typography>
          <Typography variant='body1' color='text.secondary'>
            Please review your information before completing
          </Typography>
        </Box>
      )}

      <Alert icon={<CheckCircleOutlineIcon fontSize='inherit' />} severity='success' sx={{ mb: 3 }}>
        You've reached the final step! Review your details before finishing.
      </Alert>

      <Paper
        elevation={1}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack spacing={3}>
          <Box>
            <SectionHeader title='Basic Contact' stepIndex={STEP_INDICES.BASIC_CONTACT} />
            <Typography>Phone: {basicInfo.phone}</Typography>
            {basicInfo.partnerId && (
              <Typography>Partner: {memberPartner?.name || 'NONE'}</Typography>
            )}
          </Box>

          <Divider />

          <Box>
            <SectionHeader title='Personal Details' stepIndex={STEP_INDICES.PERSONAL_DETAILS} />
            <Typography>
              Name: {personalDetails.firstName} {personalDetails.lastName}
            </Typography>
            <Typography>Email: {personalDetails?.email}</Typography>
            {personalDetails.preferredName && (
              <Typography>Preferred Name: {personalDetails.preferredName}</Typography>
            )}
            {personalDetails.dob && (
              <Typography>
                Date of Birth: {moment.parseZone(personalDetails.dob).format('ddd MMM DD YYYY')}
              </Typography>
            )}
          </Box>

         

          <Divider />
          <Box>
            <SectionHeader title='Additional Information' stepIndex={STEP_INDICES.PREFERENCE} />
            {preferenceInformation?.hopeToGain && (
              <>
                <Typography variant='subtitle2' sx={{ mt: 1 }}>
                  What do you hope to gain:
                </Typography>
                <Typography>{preferenceInformation.hopeToGain}</Typography>
              </>
            )}
            {preferenceInformation?.preferredPartnerName && (
              <>
                <Typography variant='subtitle2' sx={{ mt: preferenceInformation?.hopeToGain ? 2 : 1 }}>
                  Preferred MindRight Partner:
                </Typography>
                <Typography>{preferenceInformation.preferredPartnerName}</Typography>
              </>
            )}
            {!preferenceInformation?.hopeToGain && !preferenceInformation?.preferredPartnerName && (
              <Typography variant='body2' color='text.secondary'>
                No additional information provided
              </Typography>
            )}
          </Box>
          <Divider />
          {/* <Box>
            <SectionHeader title='Schedule' stepIndex={STEP_INDICES.SCHEDULE} />
            <Typography>
              Preferred time:{' '}
              {Object.entries(TIME_SLOTS_REVERSE_MAP).find(
                ([_, code]) => code === availability.shiftTime
              )?.[0] || availability.shiftTime}
            </Typography>
            <Typography>
              Preferred days:{' '}
              {availability.shiftDays.length > 0
                ? availability.shiftDays
                    .map(
                      (dayCode) =>
                        Object.entries(UI_DAYS_REVERSE_MAP).find(
                          ([_, code]) => code === dayCode
                        )?.[0] || dayCode
                    )
                    .join(', ')
                : 'None selected'}
            </Typography>
          </Box>

           */}
          {/* {(emergencyContact?.contactName && emergencyContact?.contactPhone) && <>
            <Divider />
            <Box>
              <SectionHeader title='Emergency Contact' stepIndex={STEP_INDICES.EMERGENCY_CONTACT} />
              <Typography>Name: {emergencyContact?.contactName}</Typography>
              <Typography>Phone: {emergencyContact?.contactPhone}</Typography>
              <Typography>Relationship: {emergencyContact?.contactRelationship}</Typography>
            </Box>
          </>} */}
        </Stack>
      </Paper>
    </Box>
  );
};

export default ReviewStep;
